/**
 * Order-related type definitions
 */
import { Timestamp } from 'firebase/firestore';
import { Address } from './user.types';

export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

export const OrderStatusLabels: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: 'Pending',
  [OrderStatus.CONFIRMED]: 'Confirmed',
  [OrderStatus.PROCESSING]: 'Processing',
  [OrderStatus.SHIPPED]: 'Shipped',
  [OrderStatus.OUT_FOR_DELIVERY]: 'Out for Delivery',
  [OrderStatus.DELIVERED]: 'Delivered',
  [OrderStatus.CANCELLED]: 'Cancelled',
  [OrderStatus.REFUNDED]: 'Refunded',
};

export const OrderStatusColors: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: 'warning',
  [OrderStatus.CONFIRMED]: 'info',
  [OrderStatus.PROCESSING]: 'info',
  [OrderStatus.SHIPPED]: 'primary',
  [OrderStatus.OUT_FOR_DELIVERY]: 'primary',
  [OrderStatus.DELIVERED]: 'success',
  [OrderStatus.CANCELLED]: 'error',
  [OrderStatus.REFUNDED]: 'error',
};

export enum PaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export enum PaymentMethod {
  COD = 'cod',
  ONLINE = 'online',
  UPI = 'upi',
}

export const PaymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.COD]: 'Cash on Delivery',
  [PaymentMethod.ONLINE]: 'Paid Online',
  [PaymentMethod.UPI]: 'UPI',
};

export const PaymentStatusLabels: Record<PaymentStatus, string> = {
  [PaymentStatus.PENDING]: 'Pending',
  [PaymentStatus.PAID]: 'Paid',
  [PaymentStatus.FAILED]: 'Failed',
  [PaymentStatus.REFUNDED]: 'Refunded',
};

export interface OrderItem {
  productId: string;
  productName: string;
  productThumbnail: string;
  productSlug: string;
  quantity: number;
  priceAtTime: number;
  sku: string;
  selectedSize?: string;
}

export interface OrderTimeline {
  status: OrderStatus;
  timestamp: Timestamp;
  note?: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  userId: string | null;
  guestEmail?: string;
  guestPhone?: string;
  items: OrderItem[];
  shippingAddress: Address;
  billingAddress?: Address;
  subtotal: number;
  shippingCost: number;
  tax: number;
  discount: number;
  couponCode?: string;
  total: number;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  transactionId?: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  razorpaySignature?: string;
  notes?: string;
  adminNotes?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  timeline: OrderTimeline[];
  estimatedDelivery?: Timestamp;
  deliveredAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface OrderFilters {
  status?: OrderStatus;
  paymentStatus?: PaymentStatus;
  userId?: string;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
}

/**
 * Data for creating a new order from the checkout service.
 * The flat form data is in validators.ts as CheckoutFormData (Zod schema).
 */
export interface CreateOrderInput {
  userId: string | null;
  items: OrderItem[];
  shippingAddress: Address;
  subtotal: number;
  shippingCost: number;
  tax: number;
  discount: number;
  total: number;
  paymentMethod: PaymentMethod;
  notes?: string;
  guestEmail?: string;
  guestPhone?: string;
}
